import {
  getLeoUuid,
  LeoUUID,
  getInt32,
  getObject,
  getString,
  getOptionalString,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TypesSortOrder } from "../types/typesSortOrder";
import { EmailRecipient, EmailRecipientDTO } from "./emailRecipient";
/* eslint-disable import/export */
export abstract class GetEmailCycleRecipientsRPC implements
  LeoRPC<GetEmailCycleRecipientsRPC.Request, GetEmailCycleRecipientsRPC.Response, GetEmailCycleRecipientsRPC.Errors.Errors>
{
  abstract execute(
    request: GetEmailCycleRecipientsRPC.Request
  ): Promise<LeoRPCResult<GetEmailCycleRecipientsRPC.Response, GetEmailCycleRecipientsRPC.Errors.Errors>>;
}

export namespace GetEmailCycleRecipientsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace SortColumn {
      export enum SortColumn {
        ADDED_ON = "ADDED_ON",
      }
    
      export interface SortColumnDTO {
        case: string;
      }
    
      export const fromDTO = (dto: SortColumnDTO): SortColumn => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestSortColumnError(
            `Invalid DTO ${dto} provided for SortColumn.SortColumn`
          );
        }
        switch (dto.case) {
          case "ADDED_ON":
            return SortColumn.ADDED_ON;
          default:
            throw new InvalidRequestSortColumnError(
              `Case ${dto.case} is not valid case of SortColumn.SortColumn`
            );
        }
      };
    
      export const toDTO = (sortColumn: SortColumn): SortColumnDTO => {
        const enumDTO = {
          case: SortColumn[sortColumn],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    scheduleId: string;
    pageIndex: number;
    itemsPerPage: number;
    sortOrder: TypesSortOrder.TypesSortOrderDTO;
    sortColumn: RequestEnums.SortColumn.SortColumnDTO;
    searchQuery?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetEmailCycleRecipientsRPCRequest: boolean = true;
  
    readonly scheduleId: LeoUUID;
    readonly pageIndex: number;
    readonly itemsPerPage: number;
    readonly sortOrder: TypesSortOrder.TypesSortOrder;
    readonly sortColumn: RequestEnums.SortColumn.SortColumn;
    readonly searchQuery: string | null;
  
    static readonly constraints = {
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 10,
      },
      "searchQuery": {
        "maxSize": 255,
        
        
        
        
      }
    }
  
    constructor(
    scheduleId: LeoUUID, 
    pageIndex: number, 
    itemsPerPage: number, 
    sortOrder: TypesSortOrder.TypesSortOrder, 
    sortColumn: RequestEnums.SortColumn.SortColumn, 
    searchQuery: string | null = null
    ) {
      this.scheduleId = scheduleId;
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
      this.sortOrder = sortOrder;
      this.sortColumn = sortColumn;
      if ( searchQuery !== undefined && searchQuery !== null) {
        Request.validateSearchQuery(searchQuery);
      }
      this.searchQuery = searchQuery;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const scheduleId = getLeoUuid(dto, "scheduleId", InvalidRequestScheduleIdError);
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      const sortOrder = TypesSortOrder.fromDTO(getObject(dto, "sortOrder", InvalidRequestSortOrderError));
      const sortColumn = RequestEnums.SortColumn.fromDTO(getObject(dto, "sortColumn", InvalidRequestSortColumnError));
      const searchQuery = getOptionalString(dto, "searchQuery", InvalidRequestSearchQueryError);
      return new Request(
        scheduleId,
        pageIndex,
        itemsPerPage,
        sortOrder,
        sortColumn,
        searchQuery
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          scheduleId: this.scheduleId.toDTO(),
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage,
          sortOrder: TypesSortOrder.toDTO(this.sortOrder),
          sortColumn: RequestEnums.SortColumn.toDTO(this.sortColumn),
      };
      if (this.searchQuery !== undefined && this.searchQuery !== null){
        dto.searchQuery = this.searchQuery;
      }
      return dto;
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 10) {
        throw new InvalidRequestItemsPerPageMinValueError(
          10,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 10.`
        );
      }
    }
    private static validateSearchQuery = (searchQuery: string): void => {
      if(searchQuery.length > 255) {
        throw new InvalidRequestSearchQueryMaxSizeError(
          255,
          `Attribute searchQuery is too long. Size is ${searchQuery.length}. Max size is 255.`
        );
      }
        if(searchQuery.trim().length === 0) {
          throw new InvalidRequestSearchQueryNonEmptyError(
            `Attribute searchQuery cannot be an empty string.`
          );
        }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetEmailCycleRecipientsRPC(scheduleId='${this.scheduleId}',pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}',sortOrder='${this.sortOrder}',sortColumn='${this.sortColumn}',searchQuery='${this.searchQuery}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    recipients: EmailRecipientDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetEmailCycleRecipientsResponse: boolean = true;
  
    readonly recipients: EmailRecipient[];
    readonly totalItems: number;
  
  
    constructor(
    recipients: EmailRecipient[], 
    totalItems: number
    ) {
      this.recipients = recipients;
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const recipients = getList<EmailRecipientDTO, EmailRecipient>(
        dto,
        "recipients",
        EmailRecipient.fromDTO,
        InvalidResponseRecipientsError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        recipients,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          recipients: this.recipients.map((e: EmailRecipient) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetEmailCycleRecipients(recipients='${this.recipients}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidScheduleId = "INVALID_SCHEDULE_ID",
    InvalidPageIndex = "INVALID_PAGE_INDEX"
  }

  export namespace Errors {
    
    export interface InvalidScheduleIdDTO {
      code: string;
    }
    
    export class InvalidScheduleId extends LeoRPCError<RPCError> {
      readonly isInvalidScheduleId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidScheduleId;
        super(code);
      }
    
    
      toDTO(): InvalidScheduleIdDTO {
        const dto: InvalidScheduleIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidScheduleId.prototype.toString = function toString(): string {
      return `GetEmailCycleRecipientsRPC.INVALID_SCHEDULE_ID()`;
    };
    
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetEmailCycleRecipientsRPC.INVALID_PAGE_INDEX()`;
    };
    

    export type Errors = InvalidScheduleId | InvalidPageIndex;
    export type ErrorsDTO = InvalidScheduleIdDTO | InvalidPageIndexDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidScheduleId:
          return new InvalidScheduleId();
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestScheduleIdError extends GetEmailCycleRecipientsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestScheduleIdError";
  }
}

export class InvalidRequestPageIndexError extends GetEmailCycleRecipientsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetEmailCycleRecipientsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidRequestSortOrderError extends GetEmailCycleRecipientsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortOrderError";
  }
}

export class InvalidRequestSortColumnError extends GetEmailCycleRecipientsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnError";
  }
}

export class InvalidRequestSortColumnAddedOnError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnAddedOnError";
  }
}

export class InvalidRequestSearchQueryError extends GetEmailCycleRecipientsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSearchQueryError";
  }
}

export class InvalidRequestSearchQueryMaxSizeError extends InvalidRequestSearchQueryError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSearchQueryMaxSizeError";
  }
}

export class InvalidRequestSearchQueryNonEmptyError extends InvalidRequestSearchQueryError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSearchQueryNonEmptyError";
  }
}

export class InvalidResponseRecipientsError extends GetEmailCycleRecipientsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRecipientsError";
  }
}

export class InvalidResponseTotalItemsError extends GetEmailCycleRecipientsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseInvalidScheduleIdError extends GetEmailCycleRecipientsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidScheduleIdError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetEmailCycleRecipientsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

